/**
 * This typeahead is written using rxjs and reactive programming, it should not be mistaken with the older
 * typeahead*.js files that exist in other folders such as GlobalResourcesMobile, GlobalResourceSMvc and
 * GlobalResources14.
 *
 * Those older modules should only be used on pages that use vanillaJs and requireJs for module loading.
 *
 * Scripts using the import {*} from "XXX" syntax for module loading should use this script.
 *
 * If you're working on anything after december 2022, THIS is the script you're looking for unless you're working
 * on a legacy script (Legacy scripts are contained on the GlobalResourcesMobile, GlobalResourceSMvc and
 * GlobalResources14 folders).
 *
 * May the force be with you.
 *
 *                ________
          _,.-Y  |  |  Y-._
      .-~"   ||  |  |  |   "-.
      I" ""=="|" !""! "|"[]""|     _____
      L__  [] |..------|:   _[----I" .-{"-.
     I___|  ..| l______|l_ [__L]_[I_/r(=}=-P
    [L______L_[________]______j~  '-=c_]/=-^
     \_I_j.--.\==I|I==_/.--L_]
       [_((==)[`-----"](==)j
          I--I"~~"""~~"I--I
          |[]|         |[]|
          l__j         l__j
          |!!|         |!!|
          |..|         |..|
          ([])         ([])
          ]--[         ]--[
          [_L]         [_L]
         /|..|\       /|..|\
        `=}--{='     `=}--{='
       .-^--r-^-.   .-^--r-^-.
~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */

import { fromEvent, merge, filter, switchMap, map, debounceTime } from 'rxjs';
import getTypeaheadSuggestions from 'api/getTypeaheadOptions';
import invalidRegexCharacters from 'constants/regexExpressions';
import StringExtensions from 'utils/stringExtensions';
import TypeaheadLocationType from 'enums/typeaheadLocationType';
import fastdom from 'fastdom';
import typeaheadKeyboardHandler from './typeaheadKeyboardHandler';

/**
 * toggleErrorMessage: Displays or hides the error message.
 * @param {HTMLElement} errorContainer HTML Container where the error is.
 * @param {Boolean} shouldDisplayError Value to determine if should display error message or not.
 */
function toggleErrorMessage(errorContainer, shouldDisplayError) {
  const form = errorContainer.closest('form');
  const errorContainers = errorContainer.parentElement.querySelectorAll('[data-typeahead-error]');

  errorContainers.forEach((container) => { container.style.display = 'none'});

  if (shouldDisplayError) {
    form.classList.add('error');
    errorContainer.style.display = 'block';
  } else {
    form.classList.remove('error');
  }
}

/**
 * replacer: Converts the match string into a bold html string.
 * @param {string} match string that was matched that will be on bold.
 * @returns The html structure to make bold the match string.
 */
function setOptionMatchInBold(match) {
  return `<b>${match}</b>`;
}

/**
 * SearchFormComponent: Creates and configures the search form component (called as typeahead).
 * Also, returns an observable about when the search input changes.
 * @param {Object} config Configuration object with the required components to make the search form work.
 * @returns The selected suggestion observable of the search input text state.
 */
function Typeahead(config) {
  const searchInput = config.searchInput;
  const suggestList = config.suggestionsContainer;
  const errorContainer = config.errorContainer;
  const clearButton = config.clearButton;
  const currentLocationSearchText = config.currentLocation?.searchText;
  const allowCurrentLocation = config.currentLocation?.isEnabled;
  const searchForm = searchInput.closest('form');

  // setting the value on initial load if available
  // we need to set the value using javascript because if the value is set on the HTML
  // it will be set as the input's default value, preventing the user from clearing the input
  // since the X icon is a button[type='reset'] which will reset the input to its default value instead of cleaning it.
  // for more: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/reset
  if(searchInput?.dataset?.value){
    searchInput.value = searchInput.dataset.value;
  }

  const searchInput$ = fromEvent(searchInput, 'input');
  const searchFocus$ = fromEvent(searchInput, 'focus');
  const clearClicked$ = fromEvent(clearButton, 'click');
  const searchEvent$ = merge(searchInput$, searchFocus$, clearClicked$);
  const selectedSuggestion$ = fromEvent(suggestList, 'click');

  const keyDownHandler = typeaheadKeyboardHandler({
    suggestList,
    searchInput,
    searchForm,
    selectedOptionIndex: -1,
    searchObserver: searchEvent$
  });

  /**
   * closeOptions: Closes the suggestion list.
   */
  function closeOptions() {
    fastdom.mutate(() => {
      suggestList.innerHTML = '';
      suggestList.setAttribute('hidden', '');
    });
  }

  const formOutClick$ = fromEvent(document, 'click').pipe(
    filter(() => !suggestList.hidden),
    filter((event) => !searchForm.contains(event.target)));
  const searchInputKeyDown$ = fromEvent(searchInput, 'keydown');

  formOutClick$.subscribe(closeOptions);
  searchInputKeyDown$.subscribe({
    next: (event) => {
      const shouldDisplayError = event.key === 'Enter' && event.target.value === '';

      if (shouldDisplayError) {
        event.preventDefault();
        toggleErrorMessage(errorContainer, shouldDisplayError);
        return;
      }

      keyDownHandler(event);
    },
  });

  /**
   * makeRegexCharacterValid: Converts an invalid character that cannot be used on a RegExp expression.
   * @param {string} match Matched string to make regex valid.
   * @returns An string with the regex valid to match the sent string.
   */
  function makeRegexCharacterValid(match) {
    const validRegexString = `[\\${match}]`;
    return validRegexString;
  }

  /**
   * insertCurrentLocationOption: Inserts current location option as the first option in the suggestions list.
   */
  const insertCurrentLocationOption = () => {
    suggestList.innerHTML = '';

    if (!allowCurrentLocation || sessionStorage.getItem('geolocationPermission') === 'false') {
      suggestList.setAttribute('hidden', '');
      return;
    }

    suggestList.innerHTML = `<li class="list-group-item list-group-item-action current-location"
                                role="option"
                                value="${currentLocationSearchText}"
                                data-current-location-search
                                data-qa="current location">
                                ${config.currentLocation.icon}
                              ${currentLocationSearchText}
                            </li>`;
  }

  /**
   * getPrimaryCounty: For cities options returned in the typeahead, we create a data attribute for the
   * primary county where the city belongs.
   * @param {Object} suggestion Typeahead suggestion to work with.
   */
  const getPrimaryCounty = (suggestion) =>
    suggestion.Type.toLowerCase() === TypeaheadLocationType.City.toString().toLowerCase() && suggestion.County
      ? `data-county="${suggestion.County}"`
      : '';

  /**
   * render: Updates the suggestion list with the new suggestions.
   * @param {Array} suggestArr Suggestion array with the options to display.
   */
  function displaySuggestions(suggestArr = []) {
    const userInput = searchInput.value;
    const regexCharacters = invalidRegexCharacters;
    const validUserInput = userInput.replace(regexCharacters, makeRegexCharacterValid);
    const highlight = new RegExp(validUserInput, 'i');

    if (allowCurrentLocation) {
      insertCurrentLocationOption();
    }

    if (suggestArr.length > 0) {
      suggestList.innerHTML += suggestArr
        .map((item, index) => {
          const suggestionContent = item.Name.replace(highlight, setOptionMatchInBold);
          const optionAdditionalAttributes = {
            City: `data-city="${item.CityName}"`,
            Zip: `data-city="${item.CityName}" data-zip="${item.Name}" data-county="${item.County}"`,
            // Given that, in County searches, the "item.name" has the value "Travis County, TX",
            // we will need to set "Travis" value, so we remove the rest of the string starting from
            // "County" using the new StringExtensions method.
            County: `data-county="${StringExtensions.removeWordFromString(item.Name, 'County')}"`,
            Community: `data-community-id="${item.Id}"`,
            MasterPlannedCommunity: `data-community-id="${item.Id}"`,
          };

          if (item.Type === 'Developer'){
            item.Type = 'MasterPlannedCommunity';
          }

          return `<li class="list-group-item list-group-item-action"
                      role="option"
                      value="${item.Name}"
                      data-value="${item.Name}"
                      data-location-type="${item.Type}"
                      data-geo-location-state="${item.State}"
                      data-market-state="${item.MarketStateAbbr}"
                      data-market-state-name="${item.MarketStateName}"
                      data-market-name="${item.MarketName}"
                      data-market-id="${item.MarketId}"
                      ${optionAdditionalAttributes[item.Type] || ''}
                      data-origin-Latitude="${item.Latitude}"
                      data-origin-Longitude="${item.Longitude}"
                      ${getPrimaryCounty(item)}
                      data-qa="typeahead-search-suggestions${index + 1}">
                    ${suggestionContent}
                  </li>`;
        })
        .join('');

        if (!allowCurrentLocation || sessionStorage.getItem('geolocationPermission') === 'false') {
          suggestList.removeAttribute('hidden');
        }
    }
  }

  if (allowCurrentLocation) {
    merge(searchInput$, searchFocus$).subscribe(() => {
      suggestList.removeAttribute('hidden');
      insertCurrentLocationOption();
    });
  }

  searchEvent$
    .pipe(
      debounceTime(100),
      switchMap((event) => {
        const userInput = event.target.value;
        if (!userInput) {
          toggleErrorMessage(errorContainer, false);

          return [];
        }

        if (!userInput.trimStart()) {
          toggleErrorMessage(errorContainer, true);

          return [];
        }

        return getTypeaheadSuggestions(event.target.value);
      })
    )
    .subscribe({
      next: (list) => {
    
        if (allowCurrentLocation && sessionStorage.getItem('geolocationPermission') !== 'false') {
          const isCurrentLocationSelected =
            currentLocationSearchText.toLowerCase().startsWith(searchInput.value.toLowerCase());

          const shouldDisplayError = list.length <= 0 && !isCurrentLocationSelected;
          toggleErrorMessage(errorContainer, shouldDisplayError);
        } else {
          const shouldDisplayError = list.length <= 0;
          toggleErrorMessage(errorContainer, shouldDisplayError);
        }

        displaySuggestions(list);
      },
    });

  const pipedObservable$ = selectedSuggestion$.pipe(
    filter((event) => event.target.closest('li')),
    map((event) => event.target.closest('li'))
  );

  pipedObservable$.subscribe((option) => {
    searchInput.value = option.innerText.trimStart();
    closeOptions();
  });

  // return the selected suggestion observable
  return pipedObservable$;
}

export default Typeahead;
